import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const Slider = ({ slides, onImageChange, onUrlChange, onSliderDataUpdate }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editingSlides, setEditingSlides] = useState([]);
  const { isSuperAdmin } = useAuth();

  useEffect(() => {
    if (slides && Array.isArray(slides)) {
      setEditingSlides([...slides]);
      // Reset current slide if it's out of bounds
      if (currentSlide >= slides.length) {
        setCurrentSlide(0);
      }
    }
  }, [slides, currentSlide]);

  const nextSlide = () => {
    if (slides && slides.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }
  };

  const prevSlide = () => {
    if (slides && slides.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    }
  };

  const handleEditAll = () => {
    setEditingSlides([...slides]);
    setIsEditing(true);
  };

  const handleAddSlide = () => {
    const existingIds = editingSlides.length > 0 ? editingSlides.map(s => s.id) : [0];
    const newSlide = {
      id: Math.max(...existingIds, 0) + 1,
      image: '/bayrak.png',
      url: 'https://example.com'
    };
    setEditingSlides([...editingSlides, newSlide]);
  };

  const handleDeleteSlide = (slideId) => {
    if (editingSlides.length > 1) { // Keep at least one slide
      setEditingSlides(editingSlides.filter(slide => slide.id !== slideId));
    }
  };

  const handleSlideImageChange = (slideId, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setEditingSlides(prev => prev.map(slide =>
        slide.id === slideId ? { ...slide, image: reader.result } : slide
      ));
    };
    reader.readAsDataURL(file);
  };

  const handleSlideUrlChange = (slideId, newUrl) => {
    setEditingSlides(prev => prev.map(slide =>
      slide.id === slideId ? { ...slide, url: newUrl } : slide
    ));
  };

  const handleSaveChanges = async () => {
    // Use the new callback to update the entire slider data
    if (onSliderDataUpdate) {
      await onSliderDataUpdate(editingSlides);
    } else {
      // Fallback to individual updates if the new callback isn't available
      editingSlides.forEach(slide => {
        const originalSlide = slides.find(s => s.id === slide.id);
        if (!originalSlide || originalSlide.image !== slide.image) {
          // Image was changed - this will be a base64 string or URL
          if (slide.image.startsWith('data:')) {
            // Convert base64 to file for the callback
            fetch(slide.image)
              .then(res => res.blob())
              .then(blob => {
                const file = new File([blob], `slide-${slide.id}.jpg`, { type: 'image/jpeg' });
                onImageChange(slide.id, file);
              });
          }
        }
        if (!originalSlide || originalSlide.url !== slide.url) {
          onUrlChange(slide.id, slide.url);
        }
      });
    }

    setIsEditing(false);
  };

  return (
    <div className="relative group">
      {isSuperAdmin && (
        <button
          onClick={handleEditAll}
          className="absolute top-4 right-4 z-10 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors opacity-0 group-hover:opacity-100"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </button>
      )}
      <div className="relative h-[400px] md:h-[500px] overflow-hidden">
        {slides && slides.length > 0 ? slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-500 ${index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-contain"
            />
            <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end justify-center pb-8">
              <div className="text-center text-white p-4">
                <a
                  href={slide.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-blue-600 text-white px-6 py-2 rounded-full hover:bg-blue-700 transition-colors text-sm"
                >
                  Keşfet
                </a>
              </div>
            </div>
          </div>
        )) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
            <p className="text-gray-600">Henüz slayt eklenmedi</p>
          </div>
        )}
      </div>

      {slides && slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full hover:bg-opacity-75 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-50 p-2 rounded-full hover:bg-opacity-75 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {isEditing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold">Slider Düzenle</h3>
              <button
                onClick={() => setIsEditing(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold">Mevcut Slaytlar</h4>
                <button
                  onClick={handleAddSlide}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Yeni Slayt Ekle</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {editingSlides.map((slide, index) => (
                  <div key={slide.id} className="border rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h5 className="font-medium">Slayt {index + 1}</h5>
                      {editingSlides.length > 1 && (
                        <button
                          onClick={() => handleDeleteSlide(slide.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Önizleme
                      </label>
                      <img
                        src={slide.image}
                        alt={`Slayt ${index + 1}`}
                        className="w-full h-32 object-cover rounded-md border"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Resim Değiştir
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            handleSlideImageChange(slide.id, file);
                          }
                        }}
                        className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-full file:border-0
                          file:text-sm file:font-semibold
                          file:bg-blue-50 file:text-blue-700
                          hover:file:bg-blue-100"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Keşfet Butonu URL'si
                      </label>
                      <input
                        type="url"
                        value={slide.url}
                        onChange={(e) => handleSlideUrlChange(slide.id, e.target.value)}
                        placeholder="https://example.com"
                        className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                İptal
              </button>
              <button
                onClick={handleSaveChanges}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Değişiklikleri Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Slider; 